// Primitives locales de la Console — ce que 9 écrans redéfinissaient chacun de leur côté.
// UTILITÉ : une pastille, un spinner et un calcul d'échéance n'ont aucune raison d'exister en
// plusieurs versions. Le pire n'était pas la répétition : c'était la DIVERGENCE (trois `daysLeft`
// dont deux comptaient à l'heure près et un au jour près — donc des résultats différents sur la
// même donnée à quelques heures d'intervalle).
//
// ⚠️ Ces primitives restent LOCALES à la Console : elles ne sont pas encore assez stables ni
// partagées par une deuxième app web pour monter dans `@orgazone/ui-web` (règle de 2, SPEC §24.3).
import type { CSSProperties, ReactNode, RefObject } from 'react';

/**
 * La référence de `useOverlayPanel` posée sur une modale dont le panneau est un `<form>`.
 * ⚠️ Le socle type son panneau en `<div>` mais n'en lit que ce qu'a tout élément (focus, recherche des
 * contrôles) : seul le TYPE se convertit, ici et une seule fois, plutôt qu'un transtypage par écran.
 */
export const formPanelRef = (ref: RefObject<HTMLDivElement | null>): RefObject<HTMLFormElement | null> =>
  ref as unknown as RefObject<HTMLFormElement | null>;

/** Codes couleur sémantiques de la maquette — un seul endroit où ils sont écrits. */
export type Tone = 'ok' | 'wa' | 'da' | 'in' | 'vi' | 'ne';
export const TONE: Record<Tone, { bg: string; fg: string }> = {
  ok: { bg: 'var(--ok-bg)', fg: 'var(--ok-fg)' }, wa: { bg: 'var(--wa-bg)', fg: 'var(--wa-fg)' },
  da: { bg: 'var(--da-bg)', fg: 'var(--da-fg)' }, in: { bg: 'var(--in-bg)', fg: 'var(--in-fg)' },
  vi: { bg: 'var(--vi-bg)', fg: 'var(--vi-fg)' }, ne: { bg: 'var(--ne-bg)', fg: 'var(--ne-fg)' },
};

// ⚠️ TROIS géométries, pas une : les écrans ont été alignés au pixel sur la maquette, les uniformiser
// déplacerait des pastilles déjà validées. On partage donc le COMPOSANT et les COULEURS, en gardant
// les trois formes existantes explicites — chacune dit d'où elle vient.
export type PillShape = 'std' | 'tight' | 'row';
const SHAPE: Record<PillShape, CSSProperties> = {
  // Listes et journaux (Blocages, Migrations, Locations, Brouillons).
  std: { fontSize: 'var(--t-sub)', fontWeight: 700, padding: '3px 10px', borderRadius: 'var(--r-xs)', whiteSpace: 'nowrap' },
  // ⚠️ GABARIT `pill()` DE LA MAQUETTE — sa SEULE géométrie de pastille (relevé 2026-08-12). Les tables
  // (Clients, Reprise, Comptes client) portaient un rectangle arrondi de 10,5 px en graisse 700 : la
  // maquette veut une CAPSULE de 11 px en graisse 600. Une pastille plus petite et plus grasse se lit
  // comme une étiquette technique ; la capsule se lit comme un état, ce qu'elle est.
  // (L'anneau interne de la maquette est posé par le composant, pour toutes les formes — voir `Pill`.)
  tight: {
    display: 'inline-flex', alignItems: 'center', gap: 5, minHeight: 23,
    fontSize: 'var(--t-meta)', fontWeight: 600, letterSpacing: '.01em', padding: '2px 10px', borderRadius: 'var(--r-pill)', whiteSpace: 'nowrap',
  },
  // Lignes de parc (Flotte, Comptes) — même resserrement, texte un demi-point plus grand.
  row: { fontSize: 'var(--t-note)', fontWeight: 700, padding: '4px 10px', borderRadius: 'var(--r-xs)', whiteSpace: 'nowrap' },
  // ⚠️ `ring` SUPPRIMÉ (2026-08-12) : il approchait la capsule de la maquette avec un liseré tiré du
  // TEXTE, alors que la vraie règle tire l'anneau du FOND — et cet anneau est désormais posé par le
  // composant pour toutes les formes. Aucune utilisation ne le référençait ; le garder aurait laissé
  // deux recettes pour un même effet, dont une fausse.
};

/**
 * Pastille d'état. `tone` pour un code sémantique (cas courant), `bg`/`fg` pour une couleur imposée
 * (états qui n'entrent pas dans la palette, ex. régime de location).
 */
export function Pill({ tone, bg, fg, shape = 'std', title, children }: {
  tone?: Tone | string;
  bg?: string;
  fg?: string;
  shape?: PillShape;
  title?: string;
  children: ReactNode;
}) {
  const c = bg && fg ? { bg, fg } : TONE[(tone ?? 'ne') as Tone] ?? TONE.ne;
  // ⚠️ ANNEAU INTERNE DE LA COULEUR DU FOND (maquette `pill()`). Je l'avais écarté comme inerte —
  // erreur : le fond est TRANSLUCIDE, donc l'anneau se superpose à lui et DOUBLE l'alpha sur 1 px
  // (.13 → ~.24). C'est lui qui dessine le liseré de la pastille ; sans lui elle paraît plate.
  // En thème clair, les fonds sont des aplats opaques : l'anneau y est réellement sans effet, comme
  // dans la maquette.
  // ⚠️ UNE BOÎTE `inline-flex` POUR TOUTES LES FORMES, comme la `pill()` de la maquette : en `inline`, la marge
  //    interne ne grandit pas la ligne qui porte la pastille, qui débordait donc sur la sous-ligne d'en dessous (l'échéance
  //    d'une location chevauchait son régime de 2 px dès que la page a pris la hauteur de ligne `normal` du dessin).
  return <span title={title} style={{ display: 'inline-flex', alignItems: 'center', ...SHAPE[shape], background: c.bg, color: c.fg, boxShadow: `inset 0 0 0 1px ${c.bg}` }}>{children}</span>;
}

/**
 * Puce de FILTRE — gabarit `chip()` de la maquette, sa seule forme de filtre (flotte, journal d'audit).
 *
 * ⚠️ La sélection se marque à la MARQUE, pas au neutre. Nos versions peignaient l'actif en `canvas` +
 * encre normale (flotte) ou en bleu `in-*` (journal) : dans les deux cas la puce choisie ne se
 * distinguait que par un fond légèrement plus clair, et rien ne disait que c'était un choix. Le teal
 * `ok` + son liseré à 35 % le disent. Et les inactives portent un vrai bord (`--ink-200`) : sans lui,
 * un filtre non choisi ne ressemble pas à un bouton et on ne pense pas à cliquer.
 */
/**
 * `tone="info"` (bleu) quand la sélection n'est PAS un jugement — fenêtre de temps, périmètre.
 * ⚠️ Le turquoise n'est pas neutre : il signifie « sain ». Sur l'écran de Santé, une durée en
 * turquoise se lit comme un verdict sur le service, à côté de tuiles qui emploient la même teinte
 * pour dire exactement ça. Le défaut reste turquoise — un filtre ordinaire ne juge rien non plus,
 * mais il ne côtoie pas une sémantique de couleur qui lui vole son sens.
 */
export function Chip({ on, onClick, tone, children }: { on: boolean; onClick: () => void; tone?: 'info'; children: ReactNode }) {
  const classes = ['chip', tone === 'info' ? 'info' : null, on ? 'active' : null].filter(Boolean).join(' ');
  return (
    <button type="button" onClick={onClick} className={classes}>
      {children}
    </button>
  );
}

/** Rotative de chargement — prend la couleur du texte parent (classe `.oz-spin`, styles.css). */
export function Spin({ size = 14 }: { size?: number }) {
  return <span className="oz-spin" style={{ width: size, height: size }} />;
}

const DAY = 86400000;
/**
 * Jours restants avant une échéance, **comptés en JOURS DE CALENDRIER** (négatif = dépassé, null = pas
 * d'échéance). ⚠️ Le comptage part de MINUIT des deux côtés : une échéance « aujourd'hui » vaut 0 quelle
 * que soit l'heure. Les versions à l'heure près donnaient « 0 j » le matin et « -1 j » le soir pour la
 * même date — deux écrans, deux vérités sur le même appareil.
 */
export function daysLeft(end: string | null | undefined): number | null {
  if (!end) return null;
  const midnight = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x.getTime(); };
  return Math.round((midnight(new Date(end)) - midnight(new Date())) / DAY);
}

/** Initiales d'un nom (2 lettres max) pour les avatars — « ? » si le nom est vide. */
export const initials = (name: string): string =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '?';

/**
 * RÉGIME DE PARC — libellé ET tonalité, en un seul endroit.
 *
 * ⚠️ Trois régimes, TROIS tons. La fiche magasin peignait « location courte » et « location longue »
 * de la même violette : deux engagements que tout oppose (un prêt de quelques jours contre un contrat
 * à échéance, avec blocage automatique au dépassement) devenaient indiscernables d'un coup d'œil,
 * alors que la Flotte les distinguait déjà. Un badge qui ne discrimine pas ne sert à rien.
 *
 * ⚠️ La couleur ne porte AUCUN jugement (ni `ok`, ni `da`) : un achat n'est pas « mieux » qu'une
 * location. On emploie donc l'informatif (bleu), le neutre-marqué (violet) et le neutre.
 */
export const REGIME: Record<'courte' | 'longue' | 'achat', { label: string; bg: string; fg: string }> = {
  courte: { label: 'Location courte', bg: 'var(--in-bg)', fg: 'var(--in-fg)' },
  longue: { label: 'Location longue', bg: 'var(--vi-bg)', fg: 'var(--vi-fg)' },
  achat: { label: 'Acheté', bg: 'var(--ne-bg)', fg: 'var(--ne-fg)' },
};

/**
 * RADICAL EAN rendu COMPLET, avec son « 2 » de tête (« 00004 » → « 200004 »).
 *
 * ⚠️ Le radical est STOCKÉ nu, sur 5 chiffres, mais ne s'affiche jamais ainsi : un gencode interne
 * s'écrit `2·RRRRR·NNNNNN·C` (SPEC §4284), et c'est ce préfixe-là qu'on lit au début d'une étiquette.
 * Montrer « 00004 » n'évoque rien de ce qu'on a sous les yeux en rayon.
 *
 * ⚠️ Fonction PARTAGÉE et non un `2${radical}` recopié : la règle avait déjà été appliquée à deux
 * écrans sur cinq, et les trois oubliés (liste des magasins, wizard de création) affichaient encore
 * le radical nu. Un rendu qui se recopie finit toujours par diverger.
 */
export const fullEanRadical = (radical: string | null | undefined): string | null =>
  (radical ? `2${radical}` : null);

/**
 * Statut d'un MAGASIN → pastille. Partagé entre la liste des magasins et le badge de la fiche :
 * la fiche re-écrivait le même mapping à la main, avec une casse déjà divergente
 * (« en service » / « En service » selon l'écran — le même état, deux graphies).
 */
export const storeStatus = (s: string): { k: Tone; label: string } =>
  (s === 'active' ? { k: 'ok', label: 'En service' }
    : s === 'provisioning' ? { k: 'wa', label: 'À mettre en service' }
      : { k: 'ne', label: s });
